// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer, { THDrawerProps } from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { AlertChangeStatusForm, AlertChangeStatusValuesForm } from './AlertChangeStatusForm';
import { CustomStatusMapType, AlertOutput, AlertInput, CaseCustomField, AlertStatusStage } from 'thehive-sdk';
import { useListAllUsers } from '@Queries/userQueries';

interface AlertChangeStatusProps extends Omit<THDrawerProps, 'title' | 'children'> {
    onEdit: (data: Partial<AlertInput>) => Promise<void | AlertOutput>;
    onFinish?: () => void;
    statusList: CustomStatusMapType;
    disabledToggleDrawer?: boolean;
    initialValues: {
        status?: string;
        summary?: string;
        customFields?: Array<CaseCustomField>;
        assignee?: string | null;
    };
    useCustomField?: boolean;
}

export const AlertChangeStatus = ({
    onEdit,
    triggerElem,
    statusList,
    initialValues,
    disabledToggleDrawer = false,
    onFinish,
    useCustomField = false,
    ...propsDrawer
}: AlertChangeStatusProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!initialValues,
        fieldsTouchedInit: !!initialValues,
    });

    const onFinishHandler = ({ status = '', summary, assignee, customFields }: AlertChangeStatusValuesForm): void => {
        const statusProcessed = status.trim();

        if (statusProcessed) {
            onEdit({ status: statusProcessed, summary: summary?.trim(), assignee, customFields });
        }
    };

    const { data: assignableUsers = [] } = useListAllUsers();

    return (
        <THDrawer
            {...propsDrawer}
            title={i(`alert.change-status.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={() => form.resetFields()}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <AlertChangeStatusForm
                    isValid={isValid && fieldsTouched}
                    form={form}
                    onChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    statusList={statusList || {}}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        if (!disabledToggleDrawer) {
                            toggleDrawer();
                        }
                        onFinish?.();
                    }}
                    onCancel={() => {
                        setFieldsTouched(false);
                        toggleDrawer();
                        form.resetFields();
                    }}
                    initialValues={initialValues}
                    assignableUsers={assignableUsers}
                    useCustomField={useCustomField && Object.keys(statusList).includes(AlertStatusStage.CLOSED)}
                />
            )}
        </THDrawer>
    );
};
